import type {
  ServiceCenterAgent,
  ServiceCenterBenefit,
} from '@/api/directus/serviceCenterAgents';
import type {EnhancedBannerAgent} from '@/components/ServiceCenterAgentBannerEnhanced/ServiceCenterAgentBannerEnhanced';
import type {BannerAgent} from '@/modules/common/components/ServiceBanner/ServiceCenterAgentBanner/ServiceCenterAgentBanner';
import type {Page} from '@/types';

import {useBreakpointUp, useCurrentBreakpoint} from '@travel-frontend/core';
import {useOpeningHours} from '@travel-frontend/ui-components';
import {addHours} from 'date-fns';
import Cookies from 'js-cookie';
import {sample} from 'lodash';
import {useEffect, useMemo, useState} from 'react';
import {boolean, is, object} from 'valibot';

import {
  createAgentTextKey,
  getServiceCenterAgents,
} from '@/api/directus/serviceCenterAgents';
import {DEFAULT_REQUEST_TIMEOUT} from '@/consts';
import {useConfig} from '@/modules/common/context/Config';
import {useQueryParams} from '@/modules/common/context/QueryParams';
import {useLocalSplit} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {
  AGENT_ID_STORAGE_KEY,
  AGENT_STORAGE_KEY,
  BENEFITS_STORAGE_KEY,
  getDevice,
  getHotline,
  getInitialState,
  isBannerEnabled,
} from '@/modules/common/hooks/useServiceCenterBanner/utils';
import {useServiceConfig} from '@/modules/common/hooks/useServiceConfig';

const ServiceCenterBannerSplitConfigSchema = object({
  hotellist: object({enabled: boolean()}),
  offerlist: object({enabled: boolean()}),
});

export type UseServiceCenterBannerResult = {
  agent?: BannerAgent & EnhancedBannerAgent;
  benefits: Array<ServiceCenterBenefit>;
  hotline: string;
  isEnabled: boolean;
  treatment: string;
  openingHours: string;
  virtualQWidgetUrl?: string;
};

export const useServiceCenterBanner = (
  page: Page
): UseServiceCenterBannerResult => {
  const currentPage = useCurrentPage();
  const currentBreakpoint = useCurrentBreakpoint();
  const config = useConfig();
  const serviceConfig = useServiceConfig();
  const split = useLocalSplit<'agentPicture' | 'enhancedAgentPicture'>(
    'FFP_4042_service_center_banner'
  );
  const isDesktop = useBreakpointUp('lg');
  const device = getDevice(currentBreakpoint, isDesktop);

  const virtualQDeviceKey = {
    sm: 'phone' as const,
    md: 'tablet' as const,
    lg: 'desktop' as const,
  }[device];
  const virtualQWidgetUrl = config.virtualQ.enabled
    ? config.virtualQ.widgetUrlByPage[currentPage][virtualQDeviceKey]
    : '';

  const splitConfig = is(ServiceCenterBannerSplitConfigSchema, split.config)
    ? split.config
    : undefined;

  // split ready and correct treatment group? else we know we do not need to request agents
  const isEnabled = isBannerEnabled(page, split.treatment, splitConfig);

  const [data, setData] = useState<{
    agent?: ServiceCenterAgent;
    benefits: Array<ServiceCenterBenefit>;
    // special state param to reexecute the useEffect hook
    // which might happen when requesting a agent by ID
    // (manual cookie value change or when we delete agents in Directus)
    // but Directus does not have an agent with that ID
    refetch?: true;
  }>(getInitialState);
  const {agent, benefits, refetch} = data;

  const [queryParams] = useQueryParams();
  const destinationId = queryParams.destinationId || 'default';
  const travelType = queryParams.travelType;

  const skipRequest = useMemo(() => {
    const sessionAgentId = Cookies.get(AGENT_ID_STORAGE_KEY);
    const textKey = createAgentTextKey(page, travelType, destinationId);
    return (
      !isEnabled ||
      (((agent && typeof agent.texts[textKey] === 'string') ||
        (agent && split.treatment === 'agentPicture')) &&
        agent &&
        sessionAgentId === agent.id)
    );
  }, [agent, destinationId, isEnabled, page, travelType, split.treatment]);

  useEffect(() => {
    // skip request when
    // feature not enabled
    // OR when we have cache AND destinationId was already requested
    // OR when we have cache AND only simple banner version
    if (skipRequest) {
      return;
    }

    const idToRequest = Cookies.get(AGENT_ID_STORAGE_KEY);
    const controller = new AbortController();

    getServiceCenterAgents({
      // if the user already has a session cookie
      // use the ID from the cookie to get the agent with that ID
      // else use id from already loaded agent
      // else no ID is used which will get all agents
      id: idToRequest,
      page,
      abortSignal: controller.signal,
      travelType,
      destinationId,
    })
      .then((responseData) => {
        if (responseData.agents.length === 0 && idToRequest) {
          // seems like the request did return an empty result
          // did we request with an ID? than probably the agent with that ID was unpusblished/deleted in Directus
          // re-do the request without ID
          Cookies.remove(AGENT_ID_STORAGE_KEY);
          localStorage.removeItem(AGENT_STORAGE_KEY);
          localStorage.removeItem(BENEFITS_STORAGE_KEY);
          setData({agent: undefined, benefits: [], refetch: true});
        }

        let newAgent: ServiceCenterAgent | undefined;
        // when we have data already available from the cache
        if (agent) {
          const [update] = responseData.agents;
          newAgent = {...update, texts: agent.texts};
          const key = createAgentTextKey(page, travelType, destinationId);
          newAgent.texts[key] = update.texts[key] ?? '';
        } else {
          // else pick random agent
          newAgent = sample(responseData.agents);
          // only update cookie when we randomly pick a new agent
          // this should lead to a new random agent after 3 hours
          if (newAgent) {
            Cookies.set(AGENT_ID_STORAGE_KEY, newAgent.id, {
              expires: addHours(new Date(), 3),
            });
          }
        }

        localStorage.setItem(AGENT_STORAGE_KEY, JSON.stringify(newAgent));
        localStorage.setItem(
          BENEFITS_STORAGE_KEY,
          JSON.stringify(responseData.benefits)
        );
        setData({agent: newAgent, benefits: responseData.benefits});
      })
      .catch(() => {
        /* noop */
      });

    const id = window.setTimeout(() => {
      controller.abort();
    }, DEFAULT_REQUEST_TIMEOUT);

    return () => {
      clearTimeout(id);
      controller.abort();
    };
    // attention refetch is in addtion to all used deps here
    // so that a refetch can be triggered when requesting with ID but no agent is returned
  }, [agent, destinationId, page, skipRequest, travelType, refetch]);

  const {long} = useOpeningHours(serviceConfig);
  const openingHours = `${long.timeFrom} – ${long.timeUntil}`;

  const hotline = useMemo(
    () => getHotline({device, page, travelType, agent}),
    [agent, device, page, travelType]
  );

  return useMemo(
    () => ({
      agent: agent
        ? {
            ...agent,
            text:
              agent.texts[
                createAgentTextKey(page, travelType, destinationId)
              ] || agent.texts[createAgentTextKey(page, travelType)],
          }
        : undefined,
      benefits,
      hotline,
      isEnabled,
      treatment: split.treatment,
      openingHours,
      virtualQWidgetUrl,
    }),
    [
      agent,
      page,
      travelType,
      destinationId,
      benefits,
      hotline,
      isEnabled,
      split.treatment,
      openingHours,
      virtualQWidgetUrl,
    ]
  );
};
